import React, { useState, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AddWorker = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    permanent_address: '',
    postal_code: '',
    work_rate: '',
    mileage_rate: '',
    latitude: '',
    longitude: ''
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const addressTimeoutRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setFormData(prevState => ({
      ...prevState,
      permanent_address: value
    }));

    if (addressTimeoutRef.current) clearTimeout(addressTimeoutRef.current);
    if (value.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    addressTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/v1/employees/places/autocomplete?input=${encodeURIComponent(value)}`
        );
        setAddressSuggestions(res.data.data.predictions || []);
        setShowSuggestions(true);
      } catch (err) {
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
  };

  const handleAddressSelect = (suggestion) => {
    setFormData(prevState => ({
      ...prevState,
      permanent_address: suggestion.description
    }));
    setAddressSuggestions([]);
    setShowSuggestions(false);
  };

  const validateEmail = (email) => {
    const regex = /^([a-zA-Z0-9_\.\-\+])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.first_name) {
      setSnackbar({
        open: true,
        message: 'Please Type First Name',
        severity: 'error'
      });
      return;
    }

    if (!formData.last_name) {
      setSnackbar({
        open: true,
        message: 'Please Type Last Name',
        severity: 'error'
      });
      return;
    }

    if (!formData.email) {
      setSnackbar({
        open: true,
        message: 'Please Type Email',
        severity: 'error'
      });
      return;
    }

    if (!validateEmail(formData.email)) {
      setSnackbar({
        open: true,
        message: 'Please Type Valid Email',
        severity: 'error'
      });
      return;
    }

    if (!formData.phone) {
      setSnackbar({
        open: true,
        message: 'Please Type Phone',
        severity: 'error'
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const companyId = localStorage.getItem('companyId');

      if (!token || !companyId) {
        setSnackbar({
          open: true,
          message: 'Authentication error. Please login again.',
          severity: 'error'
        });
        return;
      }

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/workers`, {
        ...formData,
        company_id: companyId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 1) {
        setSnackbar({
          open: true,
          message: 'New Worker Created Successfully',
          severity: 'success'
        });
        setTimeout(() => {
          navigate('/employees');
        }, 2000);
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || 'Failed to create worker',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error creating worker',
        severity: 'error'
      });
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, px: { xs: 1, sm: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 } }}>
        <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
          <IconButton
            onClick={() => navigate('/employees')}
            sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">Create New Worker</Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <div style={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  label="Home Address"
                  name="permanent_address"
                  value={formData.permanent_address}
                  onChange={handleAddressChange}
                  required
                  autoComplete="off"
                />
                {showSuggestions && addressSuggestions.length > 0 && (
                  <ul style={{
                    position: 'absolute',
                    background: '#fff',
                    border: '1px solid #ccc',
                    width: '100%',
                    zIndex: 10,
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    maxHeight: 200,
                    overflowY: 'auto'
                  }}>
                    {addressSuggestions.map((s) => (
                      <li
                        key={s.place_id}
                        onClick={() => handleAddressSelect(s)}
                        style={{ padding: '8px', cursor: 'pointer' }}
                      >
                        {s.description}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Postal Code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Hourly Rate"
                name="work_rate"
                type="number"
                value={formData.work_rate}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Mileage Rate"
                name="mileage_rate"
                type="number"
                inputProps={{ min: 0, step: 1 }}
                value={formData.mileage_rate}
                onChange={handleChange}
                required
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
            >
              Create
            </Button>
            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={() => navigate('/employees')}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddWorker; 